/**
 * `<caos-prose>` — a block of authored prose: the words of a documentation page, an about page, or
 * a landing section, set to a readable measure.
 * (Catalog: Data display › Prose — a block of authored words, set to a readable measure, for a page whose content IS the text.)
 *
 * ── WHY THIS PIECE EXISTS (CAOS-389) ────────────────────────────────────────────────────────────
 * CAOS-389 asks for the public documentation to be served from pages authored on the platform. An
 * `externalPage` section may hold ONLY component placements — `ExternalPageSectionSchema` narrows
 * the item union to components, because an external page binds no record so a field placement has
 * nothing to resolve against. So the words of a documentation page can only reach the document
 * through a component, and before this one there was no component to put them in: the library
 * ships ninety-seven pieces and every one of them is a control, a container, or a record surface.
 * the text field is an input, the inline hint explains one field, the notice banner is a condition
 * the surface is in. None of them is "the paragraphs on this page", which means a documentation
 * page could not be authored at all. This is that missing primitive.
 *
 * Those three are named in words rather than written as `<caos-…>` tags on purpose, and the reason
 * is worth a line because the obvious edit is to "fix" it back. `gen-component-placement.mjs`
 * classifies a library piece by searching every library file for its tag, so a tag written in
 * ANOTHER component's comment counts as that component reaching it. Writing the literal tags here
 * moved `inline_hint` out of `library-only` and into `component` — "reached only through another
 * component", which means composed inside a parent — on the strength of this sentence mentioning
 * it. That census exists to stop the library reading as more PROVEN than it is, so a doc
 * cross-reference must not be able to promote the piece it points at.
 *
 * ── TWO WAYS THE WORDS ARRIVE, AND WHY BOTH ARE READ ────────────────────────────────────────────
 * The same placement is drawn by two different renderers, and they deliver an input differently.
 *
 * On an INTERNAL page the host instantiates the element and applies the placement's inputs as
 * attributes (`host/src/surfaces/page-surface.ts`, `applyProps`), so the prose arrives as a `body`
 * attribute.
 *
 * On an EXTERNAL page the kernel renders markup on the server (`kernel/src/sites/render.ts`,
 * `renderPlacement`) and emits every string input TWICE on purpose: once as a `data-`prefixed
 * attribute so the element can upgrade, and once as a light-DOM `<p data-input="body">` child so
 * the page is legible with JavaScript off. That is the whole reason a public page works before its
 * bundle arrives, and it means the prose is already in the document as real paragraphs.
 *
 * So this element reads the slot FIRST and the attribute only when the slot is empty. Rendering
 * both would print the external page's prose twice — once from the children the kernel wrote and
 * again from the attribute beside them — and that duplication is invisible in a unit test that
 * only ever sets the attribute. Precedence is decided from `assignedNodes()` rather than a CSS
 * `:empty` rule, because happy-dom matches `:empty` on an element that is holding text and the
 * rule would silently inverted itself under test.
 *
 * `body` and `data-body` are both observed for the same reason: the two renderers spell the same
 * input differently, and a piece that answered to only one spelling would draw on one surface and
 * be blank on the other.
 *
 * ── PARAGRAPHS, NOT MARKUP ──────────────────────────────────────────────────────────────────────
 * A `body` attribute is split into paragraphs on a blank line and each one is set as TEXT. No
 * markup is interpreted. That is deliberate and is not a feature left out: this element is placed
 * on anonymous public pages, its value comes from metadata any org admin can author, and parsing
 * that value as HTML would make a documentation page an injection surface reachable with no
 * credential. Rich content is a separate primitive with its own sanitiser, not a flag on this one.
 *
 * Styling seams: `::part(prose)`, `::part(heading)`, `::part(body)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** The heading levels a prose block may take. */
export type CaosProseLevel = 'h2' | 'h3' | 'h4';

const LEVELS: readonly CaosProseLevel[] = ['h2', 'h3', 'h4'];

/**
 * h3 by default, because of where this piece actually sits.
 *
 * Both renderers put a heading above it already: the kernel's site renderer emits the page label as
 * an `<h1>` and each section's name as an `<h2>` (`renderSitePage`, `renderSection`), and the host
 * draws a `<caos-page-header>` at the head of an internal surface. A prose block inside a section is
 * therefore at the third level, and defaulting to `h2` would put two `<h2>`s at different depths in
 * the same document — which a screen reader reads as two sibling sections rather than a section and
 * the block inside it.
 */
const DEFAULT_LEVEL: CaosProseLevel = 'h3';

const sheet = makeSheet(`
  :host { display: block; }

  .prose {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-weight: var(--caos-font-weight-body);
    color: var(--caos-color-text);
    /* The measure is the point of the piece: a documentation paragraph running the full width of a
     * wide page is the single thing that makes long-form text unreadable. --caos-measure-document
     * is the platform's own answer for document-width text. */
    max-width: var(--caos-measure-document);
    line-height: 1.6;
  }

  .heading {
    font-family: var(--caos-font-display);
    font-weight: var(--caos-font-weight-display);
    /* Sized from the body text rather than --caos-text-h2: this heading is the third level and
     * taking the h2 token would make it the same size as the section heading above it. */
    font-size: calc(var(--caos-text-body) * 1.25);
    line-height: 1.3;
    margin: 0 0 var(--caos-space-2) 0;
    color: var(--caos-color-text);
  }

  .body { display: block; }

  /* Paragraph rhythm, applied to the paragraphs this element writes AND to the ones a renderer
   * slotted in, so prose looks the same whichever way the words arrived. */
  .body p,
  ::slotted(p) {
    margin: 0 0 var(--caos-space-3) 0;
  }
  .body p:last-child,
  ::slotted(p:last-child) {
    margin-bottom: 0;
  }
  /* A link inside prose takes the brand accent, which is the one token that means "the colour this
   * tenant acts in". There is no link token on the platform scale, and inventing one here would put
   * a name in a component that the design system does not define -- which the brand-reach test
   * refuses, correctly: a token a component reads and nothing defines is a colour that silently
   * falls back to nothing. (No backticks in here: this comment is inside a template literal, and
   * one would end it.) */
  ::slotted(a) { color: var(--caos-color-accent); }

  /* ── THE RENDERER ECHOES EVERY INPUT, NOT JUST THE PROSE ────────────────────────────────────
   * The site renderer writes a light-DOM paragraph for EVERY non-empty string input of a
   * placement, not only the one carrying words: a placement setting heading and body arrives with
   * a paragraph for each, and one setting level arrives with a paragraph holding the literal text
   * h2. Only the body paragraph is prose. The others are the same values this element draws
   * properly -- the heading as a real heading element, the level as that element's tag -- so
   * leaving them in the flow prints the heading twice and the level as a stray line of content.
   *
   * Suppressed by attribute rather than by position, so an input added later is covered without
   * this rule being revisited. A child with no data-input at all is hand-authored content and is
   * left alone. Hidden rather than removed: the light DOM is what a reader sees BEFORE this
   * element upgrades, and taking nodes out of it would make the page worse in exactly the
   * no-JavaScript case the renderer wrote them for. */
  ::slotted([data-input]:not([data-input="body"])) { display: none; }
`);

export class CaosProse extends CaosElement {
  static get observedAttributes(): string[] {
    // BOTH SPELLINGS OF EVERY INPUT, not just of `body`. The host applies a placement's inputs as
    // plain attributes; the kernel's site renderer prefixes every one of them with `data-`. An
    // earlier version observed `data-body` alone, on the assumption that only the prose came
    // through the public path — and the consequence was that a server-rendered heading was read
    // from an attribute that is never set there, so every public documentation page drew its
    // heading EMPTY. See {@link CaosProse.input}.
    return ['heading', 'data-heading', 'level', 'data-level', 'body', 'data-body'];
  }

  private prose: HTMLDivElement;
  private headingEl: HTMLHeadingElement;
  private bodyEl: HTMLDivElement;
  private slotEl: HTMLSlotElement;

  constructor() {
    super(sheet);

    this.prose = document.createElement('div');
    this.prose.className = 'prose';
    this.prose.setAttribute('part', 'prose');

    // Created as an h3 and retagged by `sync` when `level` says otherwise. A heading element's tag
    // IS its level to assistive technology, so this cannot be an attribute on one fixed tag.
    this.headingEl = document.createElement(DEFAULT_LEVEL);
    this.headingEl.className = 'heading';
    this.headingEl.setAttribute('part', 'heading');

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'body';
    this.bodyEl.setAttribute('part', 'body');

    this.slotEl = document.createElement('slot');
    // The slot decides precedence, so this element has to know when its content changes rather than
    // sampling it once at connect: an external page's children are in the markup before upgrade,
    // but a host that appends placed children after drawing the element arrives later.
    this.slotEl.addEventListener('slotchange', () => this.sync());

    this.prose.append(this.headingEl, this.bodyEl, this.slotEl);
    this.root.append(this.prose);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /**
   * One input, from whichever spelling of it the renderer used.
   *
   * The host applies a placement's inputs as plain attributes (`applyProps`); the kernel's site
   * renderer writes `data-<name>` instead, with underscores turned into hyphens. Every input this
   * element reads has to go through here, rather than only the prose — reading one of them the
   * plain way is how a server-rendered heading came back empty.
   *
   * The plain spelling wins where both are present. It is the one an author or a host set
   * deliberately; the `data-` form is the renderer's echo of the same value, so they agree in
   * practice and the order only matters if something sets one of them afterwards.
   */
  private input(name: string): string | null {
    return this.getAttribute(name) ?? this.getAttribute(`data-${name}`);
  }

  /**
   * Whether a renderer put words inside this element — real content, not the whitespace between two
   * tags.
   *
   * ── READ OFF THE LIGHT DOM, NOT OFF THE SLOT ──────────────────────────────────────────────────
   * The obvious implementation asks the slot (`assignedNodes({ flatten: true })`) and it is the
   * wrong one here. Measured: a server-rendered element — children in the markup, then upgraded —
   * reports ZERO assigned nodes at the moment `connectedCallback` runs, so the component concluded
   * nothing had been slotted in and drew the `data-body` attribute as well. That is the exact
   * double-printing this precedence exists to prevent, arriving through the check meant to prevent
   * it.
   *
   * The element's own children are the ground truth and need no slot machinery to be ready: this
   * piece has a single unnamed slot, so every child that does not name another one is content for
   * it. `slotchange` is still listened for, because in a real browser it is what reports children a
   * host appends AFTER drawing the element; this read is what makes the answer correct at upgrade.
   *
   * A text node of spaces and newlines is what formatting leaves behind, and treating it as content
   * would suppress the `body` attribute on every page whose markup is indented.
   *
   * ── AND NOT EVERY CHILD IS PROSE ──────────────────────────────────────────────────────────────
   * The renderer echoes EVERY non-empty string input as a paragraph, so a placement setting
   * `heading` arrives carrying a `<p data-input="heading">` whether or not it set `body`. Counting
   * that as content would conclude the prose had been slotted in and clear the body — leaving a
   * page with a heading, a stray copy of the heading, and no words at all. So a child that names
   * an input OTHER than `body` is the renderer talking about something this element already draws,
   * and is not prose. A child naming no input is hand-authored content and is.
   */
  private hasSlottedContent(): boolean {
    return Array.from(this.childNodes).some((n) => {
      if (n.nodeType === 3) return (n.textContent ?? '').trim() !== '';
      if (n.nodeType !== 1) return false;
      const el = n as Element;
      // There is only a default slot, so a child naming any slot at all is addressed elsewhere and
      // is not this block's prose.
      if (el.hasAttribute('slot')) return false;
      const input = el.getAttribute('data-input');
      return input === null || input === 'body';
    });
  }

  private sync(): void {
    const level = this.input('level');
    const want: CaosProseLevel = LEVELS.includes(level as CaosProseLevel) ? (level as CaosProseLevel) : DEFAULT_LEVEL;
    if (this.headingEl.tagName.toLowerCase() !== want) {
      const next = document.createElement(want);
      next.className = 'heading';
      next.setAttribute('part', 'heading');
      this.headingEl.replaceWith(next);
      this.headingEl = next;
    }

    const heading = this.input('heading') ?? '';
    this.headingEl.textContent = heading;
    // Removed rather than hidden when there is no heading: an empty heading element is still a
    // heading in the accessibility tree, and a document outline with blank entries in it is worse
    // than one with fewer.
    this.headingEl.hidden = heading.trim() === '';

    // The slot wins. See the module docblock: on an external page the words are in the children the
    // kernel wrote AND in the attribute beside them, so rendering both prints the page twice.
    if (this.hasSlottedContent()) {
      this.bodyEl.replaceChildren();
      return;
    }

    const body = this.input('body') ?? '';
    if (body.trim() === '') {
      this.bodyEl.replaceChildren();
      return;
    }
    this.bodyEl.replaceChildren(
      ...body
        .split(/\n\s*\n/)
        .map((para) => para.trim())
        .filter((para) => para !== '')
        .map((para) => {
          const p = document.createElement('p');
          // Set as text, never parsed. The value reaches here from metadata and this element is
          // drawn on anonymous public pages; see the module docblock.
          p.textContent = para.replace(/\s*\n\s*/g, ' ');
          return p;
        }),
    );
  }
}

define('caos-prose', CaosProse);

declare global {
  interface HTMLElementTagNameMap {
    'caos-prose': CaosProse;
  }
}
